import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import { Pencil, Trash2, Variable } from 'lucide-react';
import { FunnelVariableDto } from 'api/funnel';
import { Sizes } from 'enums/sizes';
import { funnelExpressionsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { useAbp } from 'core/hooks/useAbp';
import { AbpPolicies } from 'core/constants/AbpPolicies';
import { useFunnelDetail } from '../FunnelDetailContext';
import { BlockCard, StripedList } from 'back-office/shared/components/BlockCard';
import { RowActions } from 'back-office/shared/components/RowActions';
import { VariableValueModal } from '../forms/VariableValueModal';
import { DASH } from '../funnelFormat';

/**
 * Bloc « Variables » : valeurs consommées par les formules de tarification du tunnel.
 *
 * Ce sont ces mêmes variables que propose le constructeur de formule ; elles viennent du
 * contexte de la page, chargé une seule fois pour toute la vue.
 */
export const VariablesBlock: FC = () => {
  const { tunnelId, variables, reloadVariables } = useFunnelDetail();
  const { openBlankDialog } = useDialog();
  const { createMenuItem } = useActionMenu();
  const { hasPermission } = useAbp();
  // Le back exige cette permission sur les trois gestes : sans elle, ouvrir la modale ne
  // mènerait qu'à un 403 à l'enregistrement.
  const canDefineVariables = hasPermission(AbpPolicies.EXPRESSIONS_VARIABLES_DEFINED);
  const [toDelete, setToDelete] = useState<FunnelVariableDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openVariable = (variable?: FunnelVariableDto) =>
    openBlankDialog(
      { size: Sizes.xl },
      <VariableValueModal
        variable={variable}
        tunnelId={tunnelId}
        // Une variable ne se redéfinit qu'une fois par tunnel : le back refuse le
        // doublon, autant ne pas le proposer.
        usedVariableIds={variables.flatMap((v) => (v.variable?.id ? [v.variable.id] : []))}
        onSuccess={reloadVariables}
      />
    );

  const deleteVariable = async () => {
    if (!toDelete?.id) return;

    setIsDeleting(true);
    try {
      await funnelExpressionsAPI.deleteVariableValue({ variableValueId: toDelete.id });
      toast.success('Variable retirée du tunnel');
      await reloadVariables();
    } catch (error) {
      console.error('Suppression de la variable impossible', error);
      toast.error('La suppression a échoué');
    } finally {
      setIsDeleting(false);
      setToDelete(null);
    }
  };

  return (
    <>
      <BlockCard
        title="Variables"
        description="Valeurs utilisées par les formules de tarification"
        icon={<Variable className="h-4 w-4" />}
        headerExtra={
          <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">
            {variables.length}
          </span>
        }
        actions={
          canDefineVariables
            ? [createMenuItem('add', 'Ajouter une variable', () => openVariable(), { icon: 'add' })]
            : undefined
        }
        isEmpty={variables.length === 0}
        emptyLabel="Aucune variable rattachée à ce tunnel"
        flushBody
      >
        <StripedList>
          {variables.map((variable) => (
            <li key={variable.id} className="flex items-center gap-3 px-5 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="truncate text-sm font-semibold text-zinc-800">
                    {variable.variable?.libelle || DASH}
                  </p>
                  {variable.isUserConfigurable && (
                    <span
                      title="Réglable par le courtier au comparateur"
                      className="flex-shrink-0 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700"
                    >
                      courtier
                    </span>
                  )}
                  {variable.isValideurConfigurable && (
                    <span
                      title="Modifiable par le valideur des devis"
                      className="flex-shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                    >
                      valideur
                    </span>
                  )}
                </div>
                <p className="truncate font-mono text-[11px] text-gray-400">
                  {variable.variable?.code || DASH}
                </p>
              </div>

              <span className="flex-shrink-0 rounded-md bg-primary/10 px-2.5 py-1 text-sm font-bold tabular-nums text-primary">
                {variable.value ?? DASH}
                {variable.unite ? ` ${variable.unite}` : ''}
              </span>

              {canDefineVariables && (
                <RowActions
                  actions={[
                    {
                      tone: 'edit',
                      label: 'Modifier la valeur',
                      icon: <Pencil className="h-4 w-4" />,
                      onClick: () => openVariable(variable),
                    },
                    {
                      tone: 'danger',
                      label: 'Retirer la variable',
                      icon: <Trash2 className="h-4 w-4" />,
                      onClick: () => setToDelete(variable),
                    },
                  ]}
                />
              )}
            </li>
          ))}
        </StripedList>
      </BlockCard>

      <ConfirmationOverlay
        isOpen={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={deleteVariable}
        isLoading={isDeleting}
        title="Retirer cette variable ?"
        description={`La variable « ${toDelete?.variable?.libelle ?? ''} » ne sera plus disponible dans les formules de ce tunnel.`}
        confirmLabel="Retirer"
      />
    </>
  );
};
